import logger from '@finn-no/fiaas-logger';
import { Box, Button } from '@warp-ds/react';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { createItemIdForNewAd } from '@/api/api';
import type { FhhUnleashToggles } from '@/domain';
import { trackAdInsertionInfoPageView } from '@/lib/tracking/tracking';
import { clientConfig, config } from '@/server/config';
import { buildCommonProps } from '@/server/middleware';
import { getInnfinnCsrfToken } from '@/utils/csrf';

interface NewServiceInfo {
    itemId?: string;
    csrfMicroserviceToken: string;
    unleash?: FhhUnleashToggles;
}

const tjvrInfo = (actionPath: string, props: NewServiceInfo) => {
    const { baseUrl, apiExternalUrl } = useConfig();
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);

    const handleCreateNewAdClick = async () => {
        setIsLoading(true);
        try {
            const result = await createItemIdForNewAd(apiExternalUrl);
            router.push(`/annonseinnlegging/objekt/?adId=${result.id}`);
        } catch (e) {
            console.error('Failed to create item id', e);
            setIsLoading(false);
        }
    };

    return (
        <>
            <Head
                title="Hytteutleie | FINN reise"
                description="Informasjon om utleie av feriehjem og hytter i Norge"
                canonical={`${baseUrl}/reise/feriehus-hytteutleie/annonseinnlegging/info/`}
            />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/feriehus-hytteutleie" className="max-w-screen-md md:px-64 sm:px-16">
                <header>
                    <h1 className="mt-64">Utleie av feriehjem og hytter i Norge</h1>
                    <p className="mt-16">
                        Vil du tjene penger på hytta eller feriehjemmet ditt? Nå er det gratis, trygt og superenkelt å leie ut via FINN.no –
                        med inkludert forsikring og automatisk skatterapportering.
                    </p>
                </header>
                <section>
                    <h2 className="mt-40">Slik fungerer tjenesten</h2>
                    <article>
                        <h3 className="mt-16 h4">Sikker betaling for begge parter</h3>
                        <p>
                            Leietaker betaler på forhånd. Pengene oppbevares trygt i 24 timer etter innsjekking før de overføres til deg.
                            Dette sikrer at du får betalt – uten risiko.
                        </p>
                        <Box info className="mt-16">
                            <p className="p-8">Husk å holde prisene dine oppdatert – det er de som gjelder ved bestilling.</p>
                        </Box>
                    </article>
                    <article>
                        <h3 className="mt-24 h4">Inkludert Vertsforsikring fra If</h3>
                        <p>
                            Skader forårsaket av leietaker er automatisk dekket gjennom vertsforsikringen fra If. Forsikringen dekker opptil
                            3 millioner kroner, samt eventuelle tapte leieinntekter i inntil 6 måneder. Har du allerede forsikring hos If,
                            slipper du å betale egenandel.
                        </p>
                    </article>
                    <article>
                        <h3 className="mt-24 h4">Trygghet ved avbestilling</h3>
                        <p>
                            Du velger selv hvor fleksible eller strenge avbestillingsreglene dine skal være – slik at du kan tilpasse etter
                            behov og beskytte inntektene dine.
                        </p>
                    </article>
                </section>
                {props.unleash?.newAdInsertion ? (
                    <div className="mb-[200px] mt-40">
                        <Button primary onClick={handleCreateNewAdClick} loading={isLoading}>
                            Opprett annonse
                        </Button>
                    </div>
                ) : (
                    <form action={actionPath} method={props.itemId ? 'GET' : 'POST'} className="mb-[200px] mt-40">
                        <input type="hidden" name="adType" value="travel-holiday-rental" />
                        <input type="hidden" name="__csrf_token" value={props.csrfMicroserviceToken} />
                        <Button data-post-button primary type="submit">
                            Opprett annonse nå
                        </Button>
                    </form>
                )}
            </Main>
        </>
    );
};

const NewServiceInfo = (props: NewServiceInfo) => {
    const actionPath = [innfinnBasePath, props.itemId].join('');

    useEffect(() => {
        trackAdInsertionInfoPageView(props.itemId);
    }, []);

    return tjvrInfo(actionPath, props);
};

const fltCookiesFrom = (req: GetServerSidePropsContext['req']) => {
    const lookupCookie = (name: string) => (req.cookies[name] ? `${name}=${req.cookies[name]}` : null);

    const flt = lookupCookie('__flt');
    const fltDev = lookupCookie('__flt_dev');

    return [flt, fltDev].filter(Boolean).join(';');
};

export const getServerSideProps = async ({ req, res, query }: GetServerSidePropsContext) => {
    const { abroad, itemId } = query;
    const commonProps = await buildCommonProps(req, res, query);
    const baseUrl = config.get('baseUrl');

    if (itemId) {
        if (commonProps.unleash?.newAdInsertion) {
            return {
                redirect: {
                    destination: `${baseUrl}/reise/feriehus-hytteutleie/annonseinnlegging/objekt/?adId=${itemId}`,
                    permanent: false,
                    basePath: false,
                },
            };
        } else {
            return {
                redirect: {
                    destination: `${innfinnBasePath}${itemId}`,
                    permanent: false,
                    basePath: false,
                },
            };
        }
    }
    if (abroad === 'true') {
        // User clicked 'Create new ad' for abroad, i.e. should not show the page; instead create a new ad and redirect to it
        if (commonProps.unleash?.newAdInsertion) {
            let response: Response;
            const cookies = fltCookiesFrom(req);

            try {
                const url = `${config.get('apiInternalUrl')}`;
                response = await fetch(`${url}/item/create-item-id`, {
                    method: 'POST',
                    redirect: 'manual',
                    headers: { cookie: cookies },
                });
            } catch (e) {
                console.error('Failed to create item id', e);
                throw new Error('Could not create new item');
            }

            const body: { id: string } = await response.json();
            const newItemId = body.id;

            return {
                redirect: {
                    destination: `${baseUrl}/reise/feriehus-hytteutleie/annonseinnlegging/objekt/?adId=${newItemId}`,
                    permanent: false,
                    basePath: false,
                },
            };
        } else {
            const formData = new FormData();
            formData.append('adType', 'travel-holiday-rental');
            formData.append('__csrf_token', getInnfinnCsrfToken(commonProps.loginId ?? '0') || '');

            const cookies = fltCookiesFrom(req);
            let response: Response;

            try {
                response = await fetch(`${baseUrl}${innfinnBasePath}`, {
                    method: 'POST',
                    redirect: 'manual',
                    headers: { cookie: cookies },
                    body: formData,
                });
            } catch (e) {
                logger.error('Could not init new ad in innfinn', e);
                throw new Error('Could not init new ad in innfinn');
            }

            if (response.status !== 302) {
                logger.error(`Unexpected response status from innfinn: ${response.status}`);
                throw new Error('Unexpected response status from innfinn');
            }

            const location = response.headers.get('location');

            return {
                redirect: {
                    destination: location,
                    permanent: false,
                    basePath: false,
                },
            };
        }
    }

    return {
        props: {
            config: clientConfig,
            itemId: itemId ?? null,
            csrfMicroserviceToken: getInnfinnCsrfToken(commonProps.loginId ?? '0'),
            unleash: commonProps.unleash,
        },
    };
};

const innfinnBasePath = '/innfinn/adselection/ad/';

export default NewServiceInfo;
